import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Text, View, Pressable, Linking, Platform } from 'react-native';
import * as DocumentPicker from 'expo-document-picker';
import { StyleSheet } from 'react-native-unistyles';
import IconButton from '@/components/General/IconButton';
import Callout from '@/components/General/Callout';
import Unicon from '@/components/General/Unicon';
import SolidButton from '@/components/General/SolidButton';
import FilePickerModal from '@/components/Files/FilePickerModal';
import { uploadFormResource, describeFormResources, getFormResourceUrl } from '@/data/formResources';
import { getFormResourceError, normalizeResourcePaths, resourceStableId, MAX_FORM_RESOURCES, FORM_RESOURCE_ACCEPT } from '@/utils/formResourcePolicy';
import { assetToSubmission } from '@/utils/formAssets';
import { showAlert } from '@/utils/showAlert';

const basename = (path) => String(path || '').split('/').pop() || 'document';

const formatBytes = (bytes) => {
    if (!bytes || typeof bytes !== 'number') return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Builder-side form resources editor (plan §7.4). Manages the ordered
 * `resourcePaths` array — the single attachment order/source of truth.
 * Descriptors (filename/size) are fetched on demand via the form-resources
 * edge fn and cached locally; uploads stage immediately so a failed form
 * Save doesn't lose the document (it survives as a pending path).
 */
const FormResourcesEditor = ({ formID, resourcePaths = [], onChange, disabled = false, isPublic = false }) => {
    const paths = normalizeResourcePaths(resourcePaths);
    const [descriptors, setDescriptors] = useState({});
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [filePickerOpen, setFilePickerOpen] = useState(false);
    const describeGeneration = useRef(0);

    // Resolve descriptors for any paths we don't know yet. Stale responses
    // are dropped via the generation counter (rapid reorder/remove).
    useEffect(() => {
        const missing = paths.filter((p) => !descriptors[p]);
        if (!missing.length || !formID) return undefined;
        const generation = ++describeGeneration.current;
        describeFormResources({ formID, paths: missing })
            .then((list) => {
                if (generation !== describeGeneration.current) return;
                setDescriptors((prev) => {
                    const next = { ...prev };
                    for (const d of list || []) {
                        if (d?.path) next[d.path] = d;
                    }
                    return next;
                });
            })
            .catch(() => { /* rows fall back to the path basename */ });
        return () => { describeGeneration.current += 1; };
    }, [paths.join('|'), formID]);

    const handlePick = useCallback(async (picked) => {
        if (disabled || busy) return;
        const asset = Array.isArray(picked) ? picked[0] : picked;
        if (!asset) return;

        const validationError = getFormResourceError(asset, { existingCount: paths.length });
        if (validationError) {
            setError(validationError);
            return;
        }
        if (!formID) {
            setError('Save the form before attaching documents.');
            return;
        }

        setBusy(true);
        setError(null);
        try {
            const submission = await assetToSubmission(asset);
            const descriptor = await uploadFormResource({
                formID,
                filename: submission.filename,
                mimeType: submission.mimeType,
                base64: submission.base64,
            });
            if (!descriptor?.path) throw new Error('Upload did not return a path');
            setDescriptors((prev) => ({ ...prev, [descriptor.path]: descriptor }));
            onChange(normalizeResourcePaths([...paths, descriptor.path]));
        } catch (err) {
            // Staged state is untouched — the creator can retry or discard.
            setError(err?.message || 'Couldn\u2019t attach that document. Try again.');
        } finally {
            setBusy(false);
        }
    }, [disabled, busy, paths, formID, onChange]);

    // "Upload File" — same DocumentPicker contract FileInput wraps, but
    // launched from a plain button so it matches "Link a Reach File".
    const handleUploadPress = useCallback(async () => {
        if (disabled || busy) return;
        const picker = await DocumentPicker.getDocumentAsync({ multiple: false, type: FORM_RESOURCE_ACCEPT });
        if (!picker.canceled && picker.assets?.length) handlePick(picker.assets[0]);
    }, [disabled, busy, handlePick]);

    // Attach an existing organization Files item — keeps the original key (no
    // copy/move/delete of the source object). Attaching intentionally widens
    // the file's audience to everyone who can fill this form, so the picker
    // selection always acknowledges an audience warning first.
    const attachFilesRecord = useCallback((file) => {
        const key = file?.key;
        if (!key || typeof key !== 'string') {
            setError('That file could not be attached. Try uploading it instead.');
            return;
        }
        if (paths.includes(key)) {
            setError('That document is already attached.');
            return;
        }
        const validationError = getFormResourceError(
            { filename: file.filename, mimeType: file.mimeType, size: file.size },
            { existingCount: paths.length },
        );
        if (validationError) {
            setError(validationError);
            return;
        }
        setError(null);
        // Seed the descriptor from the Files record — pre-save the key isn't a
        // saved resourcePath yet, so describe would reject it as non-dedicated.
        setDescriptors((prev) => ({
            ...prev,
            [key]: { path: key, filename: file.filename, mimeType: file.mimeType, size: file.size, source: 'files' },
        }));
        onChange(normalizeResourcePaths([...paths, key]));
    }, [paths, onChange]);

    const handleFilesPick = useCallback((file) => {
        if (disabled || busy) return;
        if (!file?.key) {
            setError('That file could not be attached. Try uploading it instead.');
            return;
        }
        showAlert({
            title: 'Attach this file?',
            subtitle: isPublic
                ? `"${file.filename || 'This file'}" will be viewable by anyone with the public form link — even people who cannot access its folder.`
                : `"${file.filename || 'This file'}" will be viewable by anyone who can access this form, even if they cannot access its folder.`,
            confirmText: 'Attach',
            onConfirm: () => attachFilesRecord(file),
        });
    }, [disabled, busy, isPublic, attachFilesRecord]);

    const move = useCallback((index, direction) => {
        const next = [...paths];
        const target = index + direction;
        if (target < 0 || target >= next.length) return;
        const [moved] = next.splice(index, 1);
        next.splice(target, 0, moved);
        onChange(next);
    }, [paths, onChange]);

    const remove = useCallback((path) => {
        setError(null);
        setDescriptors((prev) => {
            const next = { ...prev };
            delete next[path];
            return next;
        });
        onChange(paths.filter((p) => p !== path));
    }, [paths, onChange]);

    const openResource = useCallback(async (path, disposition = 'inline') => {
        // Open the blank popup inside the tap gesture; navigate after signing.
        let win = null;
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
            win = window.open('', '_blank', 'noopener');
        }
        try {
            const { url } = await getFormResourceUrl({ formID, path, disposition });
            if (Platform.OS === 'web') {
                if (win) {
                    win.location.href = url;
                } else {
                    const retry = window.open(url, '_blank', 'noopener');
                    if (!retry) setError('Your browser blocked the document — allow popups and try again.');
                }
            } else {
                await Linking.openURL(url);
            }
        } catch (err) {
            win?.close?.();
            setError(err?.message || 'Couldn\u2019t open that document. Try again.');
        }
    }, [formID]);

    return (
        <View testID="form-resources-editor">
            <Text style={styles.help}>
                Documents people can open while filling out this form — waivers, schedules, instructions.
            </Text>
            {isPublic && (
                <Callout
                    type="warning"
                    message="This form is public — anyone with the link can open these documents. Only attach files safe for a public audience."
                />
            )}
            {!formID && (
                <Callout type="info" message="Save the form once before attaching documents." />
            )}

            {paths.map((path, index) => {
                const descriptor = descriptors[path];
                const name = descriptor?.filename || basename(path);
                const size = formatBytes(descriptor?.size);
                return (
                    <View key={path} style={styles.row} testID={`form-resource-row-${resourceStableId(path)}`}>
                        <Pressable style={styles.rowMain} onPress={() => openResource(path)} testID={`form-resource-open-${resourceStableId(path)}`}>
                            <Unicon name="document-outline" size={18} colorVariant="forms" />
                            <View style={styles.rowText}>
                                <Text style={styles.rowName} numberOfLines={1}>{name}</Text>
                                {(descriptor || !!size) && (
                                    <Text style={styles.rowMeta}>
                                        {[
                                            descriptor ? (descriptor.source === 'files' ? 'Reach Files' : 'Uploaded to this form') : null,
                                            size,
                                        ].filter(Boolean).join(' · ')}
                                    </Text>
                                )}
                            </View>
                        </Pressable>
                        <IconButton
                            iconName="download-outline"
                            iconSize={16}
                            type="textSecondary"
                            onPress={() => openResource(path, 'attachment')}
                            tooltipText="Download"
                            testID={`form-resource-download-${resourceStableId(path)}`}
                        />
                        {!disabled && (
                            <View style={styles.rowActions}>
                                <IconButton
                                    iconName="chevron-up-outline"
                                    iconSize={16}
                                    type="textSecondary"
                                    disabled={index === 0}
                                    onPress={() => move(index, -1)}
                                    tooltipText="Move up"
                                    testID={`form-resource-up-${resourceStableId(path)}`}
                                />
                                <IconButton
                                    iconName="chevron-down-outline"
                                    iconSize={16}
                                    type="textSecondary"
                                    disabled={index === paths.length - 1}
                                    onPress={() => move(index, 1)}
                                    tooltipText="Move down"
                                    testID={`form-resource-down-${resourceStableId(path)}`}
                                />
                                <IconButton
                                    iconName="trash-outline"
                                    iconSize={16}
                                    type="error"
                                    onPress={() => remove(path)}
                                    tooltipText="Remove document"
                                    testID={`form-resource-remove-${resourceStableId(path)}`}
                                />
                            </View>
                        )}
                    </View>
                );
            })}

            {paths.length === 0 && (
                <Text style={styles.empty} testID="form-resources-empty">No documents attached.</Text>
            )}

            {!!error && <Text style={styles.error} testID="form-resources-error">{error}</Text>}

            {!disabled && paths.length < MAX_FORM_RESOURCES && !!formID && (
                <View style={styles.addActions}>
                    <SolidButton
                        text={busy ? 'Uploading…' : 'Upload File'}
                        onPress={handleUploadPress}
                        disabled={disabled || busy}
                        variant="muted"
                        type="forms"
                        size="sm"
                        iconName="cloud-upload-outline"
                        iconBefore
                        testID="form-resources-upload"
                    />
                    <SolidButton
                        text="Link a Reach File"
                        onPress={() => setFilePickerOpen(true)}
                        disabled={disabled || busy}
                        variant="muted"
                        type="forms"
                        size="sm"
                        iconName="folder-open-outline"
                        iconBefore
                        testID="form-resource-choose-files"
                    />
                </View>
            )}
            {paths.length >= MAX_FORM_RESOURCES && (
                <Text style={styles.help}>Maximum of {MAX_FORM_RESOURCES} documents reached.</Text>
            )}

            <FilePickerModal
                isOpen={filePickerOpen}
                onClose={() => setFilePickerOpen(false)}
                onSelect={handleFilesPick}
                scope="resources"
                ownerID={null}
                title="Choose a file"
            />
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts, spacing, typography, roundness }) => ({
    help: {
        color: colors.textSecondary,
        fontFamily: fonts.body,
        fontSize: typography.caption,
        marginBottom: spacing.sm,
        marginLeft: 4,
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: colors.surface,
        borderRadius: roundness.md,
        paddingVertical: spacing.xs,
        paddingHorizontal: spacing.sm,
        marginBottom: spacing.xs,
    },
    rowMain: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.sm,
        flex: 1,
        minWidth: 0,
    },
    rowText: { flex: 1, minWidth: 0 },
    rowName: {
        color: colors.text,
        fontFamily: fonts.heading,
        fontSize: typography.body,
    },
    rowMeta: {
        color: colors.textSecondary,
        fontFamily: fonts.body,
        fontSize: typography.caption,
    },
    rowActions: { flexDirection: 'row', alignItems: 'center' },
    addActions: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, marginTop: spacing.xs },
    empty: {
        color: colors.textSecondary,
        fontFamily: fonts.body,
        fontSize: typography.caption,
        fontStyle: 'italic',
        marginLeft: 4,
        marginVertical: spacing.xs,
    },
    error: {
        color: colors.error,
        fontFamily: fonts.body,
        fontSize: typography.caption,
        paddingVertical: 4,
        marginLeft: 4,
    },
}));

export default FormResourcesEditor;
